/**
 * SAML Configuration API Service
 *
 * Calls optidev-api which proxies to auth-api for SAML management
 */

import { auth } from './firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface Organization {
  id: number;
  name: string;
  primary_domain: string;
}

export interface EmailDomain {
  id: string;
  domain: string;
  organization_id: string;
  created_by_client_id?: string;
  created_at: string;
}

export interface SAMLConfig {
  enabled: boolean;
  button_label: string;
  idp_entity_id: string;
  idp_sso_url: string;
  idp_certificate: string;
  allow_password_login: boolean;
}

export interface SAMLConfigResponse {
  organization: Organization | null;
  domains: EmailDomain[];
  saml: SAMLConfig | null;
  is_public_domain: boolean;
}

export interface SetupSAMLData {
  idp_entity_id: string;
  idp_sso_url: string;
  idp_certificate: string;
  button_label?: string;
  enabled?: boolean;
  allow_password_login?: boolean;
}

export interface SAMLSetupResponse {
  success: boolean;
  message: string;
  created_organization?: boolean;
}

/**
 * Get ID token from Firebase for authenticated requests
 */
async function getIdToken(): Promise<string> {
  const token = await auth.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
}

/**
 * Get SAML configuration for current user's organization
 */
export async function getSAMLConfig(): Promise<SAMLConfigResponse> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/config`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to get SAML config: ${response.status}`);
  }

  return response.json();
}

/**
 * Create or update SAML configuration
 */
export async function setupSAML(data: SetupSAMLData): Promise<SAMLSetupResponse> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/setup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to setup SAML: ${response.status}`);
  }

  return response.json();
}

/**
 * Get email domains for current user's organization
 */
export async function getEmailDomains(): Promise<EmailDomain[]> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/domains`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to get email domains: ${response.status}`);
  }

  return response.json();
}

/**
 * Add email domain to organization
 */
export async function addEmailDomain(domain: string): Promise<EmailDomain> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/domains`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ domain }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to add email domain: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete email domain
 */
export async function deleteEmailDomain(id: string): Promise<void> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/domains/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to delete email domain: ${response.status}`);
  }
}

/**
 * Create organization without full SAML config (for getting Service Provider info)
 */
export async function createOrganization(): Promise<{ organization: Organization }> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/organization`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to create organization: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// SAML Group Mapping Configuration
// ============================================================================

export interface GroupMapping {
  id: string;
  groupName: string;
  workspaceId: string;
  role: 'admin' | 'user' | 'viewer';
}

export interface AttributeMappings {
  groups?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  displayName?: string;
  [key: string]: string | undefined;
}

export interface SamlProvisionConfig {
  id: string;
  organizationId: string;
  enableUserCreation: boolean;
  enableUserOverride: boolean;
  unmappedWorkspaceId?: string | null;
  unmappedRole?: string | null;
  groupMappings: GroupMapping[];
  attributeMappings: AttributeMappings;
}

export interface UpdateSamlProvisionConfigData {
  enableUserCreation?: boolean;
  enableUserOverride?: boolean;
  unmappedWorkspaceId?: string | null;
  unmappedRole?: string | null;
  attributeMappings?: AttributeMappings;
}

export interface CreateGroupMappingData {
  groupName: string;
  workspaceId: string;
  role: 'admin' | 'user' | 'viewer';
}

export interface UpdateGroupMappingData {
  workspaceId?: string;
  role?: 'admin' | 'user' | 'viewer';
}

/**
 * Get SAML provisioning configuration (includes group mappings)
 */
export async function getSamlProvisionConfig(): Promise<SamlProvisionConfig> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/config/settings`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to get SAML provision config: ${response.status}`);
  }

  return response.json();
}

/**
 * Update SAML provisioning configuration (toggles + unmapped defaults)
 */
export async function updateSamlProvisionConfig(data: UpdateSamlProvisionConfigData): Promise<SamlProvisionConfig> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/config/settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to update SAML provision config: ${response.status}`);
  }

  return response.json();
}

/**
 * Add group mapping
 */
export async function addGroupMapping(data: CreateGroupMappingData): Promise<SamlProvisionConfig> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/config/mappings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to add group mapping: ${response.status}`);
  }

  return response.json();
}

/**
 * Update group mapping
 */
export async function updateGroupMapping(mappingId: string, data: UpdateGroupMappingData): Promise<SamlProvisionConfig> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/config/mappings/${mappingId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to update group mapping: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete group mapping
 */
export async function deleteGroupMapping(mappingId: string): Promise<SamlProvisionConfig> {
  const idToken = await getIdToken();

  const response = await fetch(`${API_URL}/api/saml/config/mappings/${mappingId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to delete group mapping: ${response.status}`);
  }

  return response.json();
}
